import { Check, X } from 'lucide-react'
import type { PerMetricResult } from '../types'

// Human-readable label for every rubric metric. Keep keys in sync with
// scoring/aggregate.py::METRIC_ORDER. Cards fall back to the raw id if a
// metric arrives that we don't have a label for.
const METRIC_LABELS: Record<string, string> = {
  talk_ratio: 'Talk ratio',
  longest_monologue: 'Longest monologue',
  next_step_commitment: 'Next-step commitment',
  objection_delta: 'Objection handling',
  dispo_alignment: 'Dispo alignment',
  localize_within_15s: 'Localize within 15s',
  free_word_density: 'Free-word density',
  time_to_pitch: 'Time to pitch',
  name_personalization_rate: 'Name personalization',
  seven_step_coverage: '7-step coverage',
}

const METRIC_HINTS: Record<string, string> = {
  talk_ratio: 'Agent vs prospect speech share',
  longest_monologue: 'Agent max unbroken talk',
  next_step_commitment: 'Clear next step secured',
  objection_delta: 'Objections raised vs addressed',
  dispo_alignment: 'Rubric vs VICIdial dispo',
  localize_within_15s: 'Local-area phrase in first 15s',
  free_word_density: 'Value phrases per 100 agent words',
  time_to_pitch: 'Seconds to first pitch keyword',
  name_personalization_rate: 'First-name uses per minute',
  seven_step_coverage: 'How many of the 7 steps fired',
}

// Mirror of scoring/aggregate.py::WEIGHTS so we can sort cards by weight DESC
// without a round trip. If aggregate.py changes, change this too.
export const METRIC_WEIGHTS: Record<string, number> = {
  seven_step_coverage: 0.2,
  next_step_commitment: 0.15,
  objection_delta: 0.15,
  talk_ratio: 0.1,
  longest_monologue: 0.1,
  dispo_alignment: 0.1,
  localize_within_15s: 0.05,
  free_word_density: 0.05,
  time_to_pitch: 0.05,
  name_personalization_rate: 0.05,
}

const LLM_METRICS = new Set([
  'next_step_commitment',
  'objection_delta',
  'dispo_alignment',
  'seven_step_coverage',
])

// Tiny helpers — kept local so the component stays self-contained.
function fmtNum(v: unknown, digits = 2): string | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return v.toFixed(digits)
}

function firstSentence(s: unknown): string | null {
  if (typeof s !== 'string') return null
  const trimmed = s.trim()
  if (!trimmed) return null
  const m = trimmed.match(/^[^.!?]+[.!?]/)
  return (m ? m[0] : trimmed).slice(0, 160)
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : []
}

// Build the one-line "why" using whatever fields the rubric stored in details.
// Returns null if we can't derive something useful — caller will hide the line.
function buildWhy(metric: string, result: PerMetricResult): string | null {
  const d = (result.details ?? {}) as Record<string, unknown>

  switch (metric) {
    case 'talk_ratio': {
      const pct = fmtNum(d.agent_pct, 0)
      return pct ? `agent ${pct}%` : null
    }
    case 'longest_monologue': {
      // aggregate.py stores `longest_agent_sec`; spec called it `longest_sec`.
      const sec = fmtNum(d.longest_agent_sec ?? d.longest_sec, 0)
      return sec ? `${sec}s longest agent run` : null
    }
    case 'localize_within_15s': {
      const phrase = typeof d.phrase === 'string' && d.phrase ? d.phrase : null
      return phrase ? `matched "${phrase}"` : 'no localization'
    }
    case 'free_word_density': {
      const density = fmtNum(d.density, 2)
      const found = asArray(d.phrases_found)
        .filter((p): p is string => typeof p === 'string')
        .slice(0, 3)
      const head = density ? `density ${density}` : null
      const tail = found.length ? found.join(', ') : null
      return [head, tail].filter(Boolean).join(' — ') || null
    }
    case 'time_to_pitch': {
      if (d.skipped) {
        const reason =
          typeof d.skipped === 'string' ? d.skipped : 'skipped (call type)'
        return `skipped: ${reason}`
      }
      const sec = fmtNum(d.time_sec, 0)
      if (sec === null) return d.landed === false ? 'never pitched' : null
      return `${sec}s to pitch`
    }
    case 'name_personalization_rate': {
      const rate = fmtNum(d.rate_per_min, 2)
      const name =
        typeof d.prospect_first_name === 'string' && d.prospect_first_name
          ? d.prospect_first_name
          : null
      const head = rate ? `${rate}/min` : null
      const tail = name ? `("${name}")` : null
      return [head, tail].filter(Boolean).join(' ') || null
    }
    case 'seven_step_coverage': {
      const steps = d.steps_covered
      if (typeof steps === 'number') return `${steps}/7 steps`
      return firstSentence(d.reasoning)
    }
    case 'objection_delta': {
      const delta = d.delta
      const raised = d.raised
      const addressed = d.addressed
      if (typeof delta === 'number') {
        return `${addressed ?? '?'}/${raised ?? '?'} addressed (Δ${delta})`
      }
      return firstSentence(d.reasoning)
    }
    case 'next_step_commitment': {
      if (typeof d.next_action === 'string' && d.next_action) {
        return d.next_action.slice(0, 160)
      }
      return firstSentence(d.reasoning)
    }
    case 'dispo_alignment': {
      const predicted =
        typeof d.predicted_dispo === 'string' ? d.predicted_dispo : null
      const vici = typeof d.vici_dispo === 'string' ? d.vici_dispo : null
      if (predicted && vici) return `${vici} → ${predicted}`
      return firstSentence(d.reasoning)
    }
    default:
      return firstSentence(d.reasoning) ?? firstSentence(d.summary)
  }
}

// Numeric value displayed under the why line. Prefers details.metric (rubric
// dataclasses with a `.metric` property write it out via _details_as_dict),
// falls back to PerMetricResult.value.
function pickValue(result: PerMetricResult): number | null {
  const d = (result.details ?? {}) as Record<string, unknown>
  const fromDetails = d.metric
  if (typeof fromDetails === 'number' && Number.isFinite(fromDetails)) {
    return fromDetails
  }
  if (typeof result.value === 'number' && Number.isFinite(result.value)) {
    return result.value
  }
  return null
}

export function MetricCard({
  metric,
  result,
}: {
  metric: string
  result: PerMetricResult
}) {
  const label = METRIC_LABELS[metric] ?? metric
  const hint = METRIC_HINTS[metric]
  const passed = result.passed
  const why = buildWhy(metric, result)
  const value = pickValue(result)
  const isLlm = LLM_METRICS.has(metric)
  // Only `latency_ms` is exposed on PerMetricResult today (token usage gets
  // stripped server-side in _details_as_dict). Show what we have.
  const latency =
    typeof result.latency_ms === 'number' && result.latency_ms > 0
      ? Math.round(result.latency_ms)
      : null

  return (
    <div
      className={`rounded-lg border p-3 ${
        passed
          ? 'border-emerald-800/60 bg-emerald-950/30'
          : 'border-red-800/60 bg-red-950/20'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-slate-200">{label}</p>
          {hint && <p className="mt-0.5 text-[10px] text-slate-500">{hint}</p>}
        </div>
        <div
          className={`flex h-5 w-5 items-center justify-center rounded-full ${
            passed ? 'bg-emerald-600' : 'bg-red-600'
          }`}
          role="img"
          aria-label={passed ? 'passed' : 'failed'}
        >
          {passed ? (
            <Check className="h-3 w-3 text-white" />
          ) : (
            <X className="h-3 w-3 text-white" />
          )}
        </div>
      </div>
      {value !== null && (
        <p className="mt-2 font-mono text-xs text-slate-300">
          {value.toFixed(2)}
        </p>
      )}
      {why && (
        <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-400">
          {why}
        </p>
      )}
      {isLlm && (latency !== null || result.llm_model) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {result.llm_model && (
            <span className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 font-mono text-[9px] text-slate-400">
              {result.llm_model}
            </span>
          )}
          {latency !== null && (
            <span className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 font-mono text-[9px] text-slate-400">
              {latency}ms
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export function MetricBarRow({
  metric,
  result,
}: {
  metric: string
  result: PerMetricResult
}) {
  const label = METRIC_LABELS[metric] ?? metric
  const passed = result.passed
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-40 text-xs text-slate-300">{label}</div>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-800">
        <div
          className={`h-full ${passed ? 'bg-emerald-500' : 'bg-red-500'}`}
          style={{ width: passed ? '100%' : '15%' }}
        />
      </div>
      <div className="w-16 text-right font-mono text-xs text-slate-400">
        {passed ? 'pass' : 'fail'}
      </div>
    </div>
  )
}
